import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkerPortalChat } from "@/features/chat";
import { WorkerConversationList } from "@/features/workers/components/WorkerConversationList";
import { useUnifiedWorkerCommunications } from "@/features/workers/hooks/useUnifiedWorkerCommunications";
import { useWorkerMessages } from "@/features/workers/hooks/useWorkerMessages";
import { functions } from "@/firebase";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { httpsCallable } from "firebase/functions";
import { ArrowLeft, ExternalLink, MessageCircle, MessageSquare, Phone, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/admin/worker_messages/$workerId/")({
  component: WorkerMessageDetailPage,
});

function WorkerMessageDetailPage() {
  const { workerId } = Route.useParams();
  const { userProfile } = useAuth();

  // Load all conversations for the sidebar
  const {
    conversations,
    loading: conversationsLoading,
    totalUnreadCount,
  } = useUnifiedWorkerCommunications(userProfile?.uid);

  // Load WorkerMessages data
  const {
    conversation: workerMessagesConv,
    loading: wmLoading,
  } = useWorkerMessages(workerId);

  // Load PortalChats worker-sms for this worker
  const {
    chat: portalChat,
    messages: portalMessages,
    loading: pcLoading,
    sending: pcSending,
    sendMessage: sendPortalMessage,
  } = useWorkerPortalChat(workerId, userProfile?.uid);

  const hasWorkerMessages = !!workerMessagesConv;
  const hasPortalChat = !!portalChat;

  // Determine default tab - prefer the one with more recent activity
  const [activeTab, setActiveTab] = useState<"messages" | "sms">(() => {
    return "messages"; // Default, will be updated when data loads
  });

  // Update default tab based on which has more recent activity
  useEffect(() => {
    if (!wmLoading && !pcLoading) {
      if (hasWorkerMessages && hasPortalChat) {
        const wmLastAt = workerMessagesConv?.lastMessageAt?.toMillis() || 0;
        const pcLastAt = portalChat?.updatedAt?.toMillis() || 0;
        setActiveTab(pcLastAt > wmLastAt ? "sms" : "messages");
      } else if (hasPortalChat) {
        setActiveTab("sms");
      } else {
        setActiveTab("messages");
      }
    }
  }, [wmLoading, pcLoading, hasWorkerMessages, hasPortalChat, workerMessagesConv, portalChat]);

  const loading = wmLoading || pcLoading;

  // Get worker info from either source
  const workerName =
    workerMessagesConv?.workerName || portalChat?.workerInfo?.name || "Worker";
  const workerPhone =
    workerMessagesConv?.workerPhoneNumber ||
    portalChat?.workerInfo?.phoneNumber ||
    "";

  if (!userProfile) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-gray-50">
        {/* Header */}
        <div className="shrink-0 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold">Worker Messages</h1>
                <p className="text-sm text-gray-600">
                  {totalUnreadCount > 0
                    ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? "s" : ""}`
                    : "Chat with workers"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Layout with loading state */}
        <div className="mx-auto flex w-full max-w-7xl flex-1 gap-4 overflow-hidden p-4 sm:p-6">
          {/* Conversation List - Hidden on mobile when viewing a conversation */}
          <div className="hidden w-80 shrink-0 overflow-hidden rounded-lg border bg-white shadow-sm md:block">
            <WorkerConversationList
              conversations={conversations}
              selectedWorkerId={workerId}
              loading={conversationsLoading}
            />
          </div>

          {/* Loading state */}
          <div className="flex flex-1 items-center justify-center rounded-lg border bg-white shadow-sm">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-green-600" />
          </div>
        </div>
      </div>
    );
  }

  if (!hasWorkerMessages && !hasPortalChat) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-gray-50">
        {/* Header */}
        <div className="shrink-0 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold">Worker Messages</h1>
                <p className="text-sm text-gray-600">
                  {totalUnreadCount > 0
                    ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? "s" : ""}`
                    : "Chat with workers"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Layout with not found state */}
        <div className="mx-auto flex w-full max-w-7xl flex-1 gap-4 overflow-hidden p-4 sm:p-6">
          {/* Conversation List - Hidden on mobile when viewing a conversation */}
          <div className="hidden w-80 shrink-0 overflow-hidden rounded-lg border bg-white shadow-sm md:block">
            <WorkerConversationList
              conversations={conversations}
              selectedWorkerId={workerId}
              loading={conversationsLoading}
            />
          </div>

          {/* Not found state */}
          <div className="flex flex-1 items-center justify-center rounded-lg border bg-white shadow-sm">
            <div className="text-center text-gray-500">
              <MessageCircle className="mx-auto mb-4 h-16 w-16 text-gray-300" />
              <h3 className="mb-2 text-lg font-medium">Conversation not found</h3>
              <p className="text-sm">This worker hasn't sent any messages yet</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showTabs = hasWorkerMessages && hasPortalChat;
  const wmUnread = workerMessagesConv?.unreadCount || 0;
  const pcUnread = portalChat?.unreadCounts[userProfile?.uid || ""] || 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold">Worker Messages</h1>
              <p className="text-sm text-gray-600">
                {totalUnreadCount > 0
                  ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? "s" : ""}`
                  : "Chat with workers"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversation Layout */}
      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-4 overflow-hidden p-4 sm:p-6">
        {/* Conversation List - Hidden on mobile when viewing a conversation */}
        <div className="hidden w-80 shrink-0 overflow-hidden rounded-lg border bg-white shadow-sm md:block">
          <WorkerConversationList
            conversations={conversations}
            selectedWorkerId={workerId}
            loading={conversationsLoading}
          />
        </div>

        {/* Conversation View */}
        <div className="flex-1 overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="flex h-full flex-col overflow-hidden">
            {/* Tabs or Single View */}
            {showTabs ? (
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as "messages" | "sms")}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <div className="shrink-0 border-b bg-white px-4">
                  <TabsList className="h-12 w-full justify-start gap-4 rounded-none bg-transparent p-0">
                    <TabsTrigger
                      value="messages"
                      className="relative h-12 rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-green-600 data-[state=active]:text-green-600"
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Messages
                      {wmUnread > 0 && (
                        <Badge className="ml-2 bg-green-600">{wmUnread}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="sms"
                      className="relative h-12 rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:text-blue-600"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      SMS Chat
                      {pcUnread > 0 && (
                        <Badge className="ml-2 bg-blue-600">{pcUnread}</Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent
                  value="messages"
                  className="mt-0 flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
                >
                  <WorkerMessagesView
                    conversation={workerMessagesConv!}
                    workerId={workerId}
                    userProfile={userProfile}
                  />
                </TabsContent>

                <TabsContent
                  value="sms"
                  className="mt-0 flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
                >
                  <PortalChatSmsView
                    messages={portalMessages}
                    sending={pcSending}
                    onSendMessage={sendPortalMessage}
                    currentUserId={userProfile?.uid || ""}
                    currentUserName={userProfile?.name || ""}
                    workerName={workerName}
                    workerPhone={workerPhone}
                    workerId={workerId}
                  />
                </TabsContent>
              </Tabs>
            ) : hasWorkerMessages ? (
              <WorkerMessagesView
                conversation={workerMessagesConv!}
                workerId={workerId}
                userProfile={userProfile}
              />
            ) : (
              <PortalChatSmsView
                messages={portalMessages}
                sending={pcSending}
                onSendMessage={sendPortalMessage}
                currentUserId={userProfile?.uid || ""}
                currentUserName={userProfile?.name || ""}
                workerName={workerName}
                workerPhone={workerPhone}
                workerId={workerId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Worker Messages View Component (clock-in initiated messages)
function WorkerMessagesView({
  conversation,
  workerId,
  userProfile,
}: {
  conversation: NonNullable<ReturnType<typeof useWorkerMessages>["conversation"]>;
  workerId: string;
  userProfile: any;
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation.messages]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (conversation.unreadCount > 0) {
      const markAsRead = async () => {
        try {
          const markMessagesAsReadCallable = httpsCallable(
            functions,
            "markMessagesAsRead"
          );
          await markMessagesAsReadCallable({ workerId });
        } catch (error) {
          console.error("Error marking messages as read:", error);
        }
      };
      markAsRead();
    }
  }, [conversation.unreadCount, workerId]);

  const handleBack = () => {
    router.navigate({ to: "/dashboard/admin/worker_messages" });
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim() || !userProfile) {
      return;
    }

    setSending(true);

    try {
      const sendReplyFromAdminCallable = httpsCallable(
        functions,
        "sendReplyFromAdmin"
      );
      const result = await sendReplyFromAdminCallable({
        workerId,
        message: message.trim(),
        adminName: userProfile.name,
      });

      const data = result.data as { success: boolean; error?: string };

      if (data.success) {
        setMessage("");
        toast.success("Reply sent! Worker will receive an SMS notification.");
      } else {
        throw new Error(data.error || "Failed to send reply");
      }
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b bg-white p-4">
        <Button variant="ghost" size="icon" onClick={handleBack} className="md:hidden">
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <Avatar className="h-10 w-10 bg-green-100 text-green-700">
          <AvatarFallback className="bg-green-100 text-green-700">
            {conversation.workerName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold text-gray-900">
            {conversation.workerName}
          </h2>
          <p className="truncate text-sm text-gray-500">
            {formatPhoneNumber(conversation.workerPhoneNumber)}
          </p>
        </div>

        <Link
          to="/dashboard/admin/workers/$workerId"
          params={{ workerId }}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          View Profile
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
          {conversation.messages.length > 0 ? (
            <div className="space-y-3">
              {conversation.messages.map((msg) => {
                const isAdmin = msg.sender === "admin";
                return (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex",
                      isAdmin ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm",
                        isAdmin
                          ? "bg-blue-600 text-white"
                          : "border bg-white text-gray-900"
                      )}
                    >
                      {isAdmin && msg.senderName && (
                        <p className="mb-0.5 text-xs font-medium text-blue-100">
                          {msg.senderName}
                        </p>
                      )}
                      {msg.imageUrl && (
                        <img
                          src={msg.imageUrl}
                          alt="Attachment"
                          className="mb-2 max-h-64 cursor-pointer rounded-lg"
                          onClick={() => window.open(msg.imageUrl, "_blank")}
                        />
                      )}
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {msg.text}
                      </p>
                      <p
                        className={cn(
                          "mt-1 text-[11px]",
                          isAdmin ? "text-blue-100" : "text-gray-500"
                        )}
                      >
                        {msg.timestamp.toDate().toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-gray-500">No messages yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="shrink-0 border-t bg-white px-4 py-4 shadow-lg sm:px-6">
        <div className="mx-auto max-w-4xl">
          <form onSubmit={handleSendReply} className="flex gap-3">
            <div className="flex-1">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your reply..."
                className="min-h-[56px] resize-none rounded-2xl border-gray-300 px-4 py-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                maxLength={500}
                disabled={sending}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSendReply(e);
                  }
                }}
              />
              <div className="mt-1.5 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Worker will receive an SMS notification • {message.length}/500
                </p>
              </div>
            </div>
            <Button
              type="submit"
              disabled={!message.trim() || sending}
              size="icon"
              className="h-14 w-14 shrink-0 rounded-full"
            >
              {sending ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}

// Portal Chat SMS View Component (two-way SMS chat)
function PortalChatSmsView({
  messages,
  sending,
  onSendMessage,
  currentUserId,
  currentUserName,
  workerName,
  workerPhone,
  workerId,
}: {
  messages: ReturnType<typeof useWorkerPortalChat>["messages"];
  sending: boolean;
  onSendMessage: (content: string, senderName: string) => Promise<void>;
  currentUserId: string;
  currentUserName: string;
  workerName: string;
  workerPhone: string;
  workerId: string;
}) {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const handleBack = () => {
    router.navigate({ to: "/dashboard/admin/worker_messages" });
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      return;
    }

    try {
      await onSendMessage(message.trim(), currentUserName);
      setMessage("");
      toast.success("SMS sent to worker!");
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast.error("Failed to send SMS. Please try again.");
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b bg-white p-4">
        <Button variant="ghost" size="icon" onClick={handleBack} className="md:hidden">
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <Avatar className="h-10 w-10 bg-green-100 text-green-700">
          <AvatarFallback className="bg-green-100 text-green-700">
            {workerName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold text-gray-900">{workerName}</h2>
          <p className="truncate text-sm text-gray-500">
            {formatPhoneNumber(workerPhone)}
          </p>
        </div>

        <Link
          to="/dashboard/admin/workers/$workerId"
          params={{ workerId }}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          View Profile
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
          {messages.length > 0 ? (
            <div className="space-y-3">
              {messages.map((msg) => {
                const isOutbound =
                  msg.type === "sms-outbound" || msg.senderId === currentUserId;
                const isInbound = msg.type === "sms-inbound";
                return (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex",
                      isOutbound ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm",
                        isOutbound
                          ? "bg-blue-600 text-white"
                          : "border bg-white text-gray-900"
                      )}
                    >
                      {isOutbound && msg.senderName && (
                        <p className="mb-0.5 text-xs font-medium text-blue-100">
                          {msg.senderName}
                        </p>
                      )}
                      {isInbound && (
                        <p className="mb-0.5 text-xs font-medium text-gray-500">
                          SMS from worker
                        </p>
                      )}
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {msg.content}
                      </p>
                      <p
                        className={cn(
                          "mt-1 text-[11px]",
                          isOutbound ? "text-blue-100" : "text-gray-500"
                        )}
                      >
                        {msg.timestamp?.toDate().toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-gray-500">
                No SMS messages yet. Send a message to start the conversation.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="shrink-0 border-t bg-white px-4 py-4 shadow-lg sm:px-6">
        <div className="mx-auto max-w-4xl">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <div className="flex-1">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your SMS message..."
                className="min-h-[56px] resize-none rounded-2xl border-gray-300 px-4 py-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                maxLength={500}
                disabled={sending}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              <div className="mt-1.5 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Sends SMS directly to worker's phone • {message.length}/500
                </p>
              </div>
            </div>
            <Button
              type="submit"
              disabled={!message.trim() || sending}
              size="icon"
              className="h-14 w-14 shrink-0 rounded-full"
            >
              {sending ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
